import type { Column } from "@shared/types/data-table"
import type { Dispute } from "@features/disputes/types/disputes.types"
import type { ColumnManagementConfig } from "@shared/lib/tables/utils/column-management"
import {
  createStatusColumn,
  createDateColumn,
  createCustomColumn,
  createTruncatedColumn
} from "./column-factories"
import { CurrencyCell, DynamicDateCell } from "../cells/common-cells"

export const DISPUTE_COLUMNS: Record<string, Column<Dispute>> = {
  amount: createCustomColumn<Dispute>(
    "amount",
    "Amount",
    120,
    (dispute: Dispute) => (
      <CurrencyCell
        amount={dispute.amount}
        currency={dispute.currency}
        decimals={2}
        align="left"
        className="text-sm min-w-[68px]"
      />
    )
  ),
  status: createStatusColumn<Dispute>("dispute"),
  customer: createTruncatedColumn<Dispute>(
    "customer",
    "Customer",
    200,
    (dispute) => dispute.customer?.email || dispute.customer?.name || "Unknown",
    "text-muted-foreground group-hover:text-foreground font-normal"
  ),
  reason: createTruncatedColumn<Dispute>(
    "reason",
    "Reason",
    180,
    (dispute) => dispute.reason?.replace(/_/g, " ") || "Unknown",
    "text-muted-foreground group-hover:text-foreground font-normal capitalize"
  ),
  paymentId: createTruncatedColumn<Dispute>(
    "paymentId",
    "Payment",
    180,
    (dispute) => dispute.paymentId || "—",
    "text-muted-foreground group-hover:text-foreground font-mono text-xs"
  ),
  evidenceDueBy: createDateColumn<Dispute>("evidenceDueBy", "Evidence due", 150),
  created: createDateColumn<Dispute>("created", "Created", 180),
  lastUpdated: createDateColumn<Dispute>("lastUpdated", "Last Updated", 150),
  evidenceSubmittedAt: createCustomColumn<Dispute>(
    "evidence" as keyof Dispute,
    "Evidence submitted",
    160,
    (dispute: Dispute) => (
      <DynamicDateCell
        timestamp={dispute.evidence?.submittedAt as unknown as string}
        placeholder="Not submitted"
        className="text-muted-foreground group-hover:text-foreground"
      />
    )
  ),
}

export const DISPUTE_STATUS_CONFIG = {
  all: ["amount", "status", "customer", "reason", "evidenceDueBy", "created"],
  needs_response: ["amount", "status", "customer", "reason", "evidenceDueBy", "created"],
  under_review: ["amount", "status", "customer", "reason", "evidenceDueBy", "evidenceSubmittedAt", "created"],
  won: ["amount", "status", "customer", "reason", "created", "lastUpdated"],
  lost: ["amount", "status", "customer", "reason", "created", "lastUpdated"],
  charge_refunded: ["amount", "status", "customer", "reason", "created", "lastUpdated"],
} as const

export const getDisputeColumns = (status: string = 'all'): Column<Dispute>[] => {
  const keys = DISPUTE_STATUS_CONFIG[status as keyof typeof DISPUTE_STATUS_CONFIG] || DISPUTE_STATUS_CONFIG.all
  return keys.map(key => DISPUTE_COLUMNS[key]).filter(Boolean)
}

export const getDisputeColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(DISPUTE_STATUS_CONFIG[status as keyof typeof DISPUTE_STATUS_CONFIG] || DISPUTE_STATUS_CONFIG.all)
  return Object.entries(DISPUTE_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ["amount", "status"].includes(key),
    order: index
  }))
}